import { FC, useEffect, useRef, useState } from 'react';

import { TooltipArrow, tooltipArrowSize } from '@eneco/custom-components/TooltipArrow';
import { useFormatter } from '@eneco/i18n';
import { Box, Grid, Text } from '@eneco/sparky';
import { useContent } from '@sitecore/common';
import { DynamicPricingTariffsRendering } from '@sitecore/types/DynamicPricingTariffs';
import { Tooltip as VisXTooltip } from '@visx/tooltip';

import { useDynamicPricingTariffsOptional } from '../../../../hooks/useDynamicPricingTariffs';

export type TooltipData = {
  start: Date;
  total: number;
  wholesale: number;
  commercial: number;
  energytax: number;
  x: number;
  y: number;
};

type Props = {
  isOpen: boolean;
  data: TooltipData | undefined;
  onClose: () => void;
  canvasHeight: number;
  canvasWidth: number;
};

const estimatedToolTipHeight = 175;
const estimatedToolTipWidth = 150;

export const Tooltip: FC<Props> = ({ isOpen, data, canvasWidth }) => {
  const context = useDynamicPricingTariffsOptional();
  const ref = useRef<HTMLDivElement>(null);
  const { fields } = useContent<DynamicPricingTariffsRendering>();
  const { currency } = useFormatter();
  const [tooltipHeight, settooltipHeight] = useState(estimatedToolTipHeight);
  const [tooltipWidth, settooltipWidth] = useState(estimatedToolTipWidth);
  useEffect(() => {
    settooltipHeight((ref?.current?.clientHeight ?? estimatedToolTipHeight) + tooltipArrowSize);
    settooltipWidth(ref?.current?.clientWidth ?? estimatedToolTipWidth);
  }, [data]);

  if (!isOpen || !data || !context) return null;

  const { selectedProduct, showTotal } = context;
  const totalPrice = showTotal
    ? data.total && currency.euroFiveDecimals(data.total)
    : data.wholesale && currency.euroFiveDecimals(data.wholesale);

  const paddingY = 24;

  let left = data.x - tooltipWidth / 2 - tooltipArrowSize;
  let arrowOffset = tooltipWidth / 2 - tooltipArrowSize - 1;

  const isOverflowRight = left + tooltipWidth >= canvasWidth;
  const isOverflowLeft = left < 0;
  const overflowOffSet = tooltipWidth / 4 + 12;

  if (isOverflowRight) {
    left -= overflowOffSet;
    arrowOffset += overflowOffSet;
  }

  if (isOverflowLeft) {
    left += overflowOffSet;
    arrowOffset -= overflowOffSet;
  }

  const TooltipProps = {
    style: {
      marginLeft: `${arrowOffset}px`,
    },
  };

  return (
    <VisXTooltip top={data.y - tooltipHeight - paddingY} left={left} unstyled applyPositionStyle>
      <Box padding="2" backgroundColor="backgroundDark" borderRadius="s" ref={ref}>
        <Grid rowGap={1} columnGap={2} gridTemplateColumns={'max-content max-content'}>
          {totalPrice ? (
            <>
              {showTotal ? (
                <Grid.Item gridColumn="1/-1">
                  <Text size={'BodyS'} color="textInverted" weight="bold" whiteSpace="nowrap">
                    {`${totalPrice}/${
                      selectedProduct === 'electricity'
                        ? fields?.graphUnitKwhText?.value
                        : fields?.graphUnitCubicMetreText?.value
                    }`}
                  </Text>
                </Grid.Item>
              ) : null}
              <Text size="BodyXS" color="textInverted" weight="bold">
                {fields?.wholesaleTariffText?.value}
              </Text>
              <Text size="BodyXS" color="textInverted" weight="bold">
                {currency.euroFiveDecimals(data.wholesale)}
              </Text>
              {showTotal ? (
                <>
                  <Text size="BodyXS" color="textInverted" weight="bold">
                    {fields?.energytaxTariffText?.value}
                  </Text>
                  <Text size="BodyXS" color="textInverted" weight="bold">
                    {currency.euroFiveDecimals(data.energytax)}
                  </Text>
                  <Text size="BodyXS" color="textInverted" weight="bold">
                    {fields?.commercialTariffText?.value}
                  </Text>
                  <Text size="BodyXS" color="textInverted" weight="bold">
                    {currency.euroFiveDecimals(data.commercial)}
                  </Text>
                </>
              ) : null}
            </>
          ) : (
            <Text size="BodyXS" color="textInverted" weight="bold">
              {fields?.unknownTariffLabel?.value}
            </Text>
          )}
        </Grid>
      </Box>
      <TooltipArrow {...TooltipProps} />
    </VisXTooltip>
  );
};
